import type { Candle, Timeframe } from "../types";
import { RSI } from "../indicators/classic";
import { TickDominance, TickImbalance, TickSpeed } from "../indicators/tick";
import { StructureEngine } from "../structure/StructureEngine";
import { T50Engine } from "../strategies/t50/T50Engine";
import type { FVG, LiquidityLevel, StructureResult } from "../structure/types";
import { AlertDef, AlertFire, alertMeta } from "./types";

const ALL_FLAGS = { swings: true, bos: true, choch: true, cisd: true, t50: true, fvg: true, ob: false, liq: true };
const MAX_SEEN = 2000;

interface AlertState {
  primed: boolean;
  lastValue: number | null;
  wasTrue: boolean;
  lastFireMs: number;
  seen: Set<string>;
  pendingGaps: Map<string, FVG & { time: number }>;
  knownLevels: Map<string, LiquidityLevel>;
  done: boolean;
}

interface Ctx {
  candles: Candle[];
  tf: Timeframe;
  last: Candle;
  structure: StructureResult | null;
  series: Map<string, (number | null)[]>;
}

const fmt = (v: number) => (Math.abs(v) >= 1000 ? v.toFixed(2) : v.toFixed(4));

function fresh(): AlertState {
  return { primed: false, lastValue: null, wasTrue: false, lastFireMs: 0, seen: new Set(), pendingGaps: new Map(), knownLevels: new Map(), done: false };
}

function remember(set: Set<string>, key: string) {
  set.add(key);
  if (set.size > MAX_SEEN) { const it = set.values(); for (let i = 0; i < 200; i++) set.delete(it.next().value as string); }
}

function dirOk(cond: string, dir: string) { return cond === "any" || cond === dir; }

// The ONE alert evaluator. Runs unchanged in the browser and inside the backend worker (embedded V8).
// Uses the same StructureEngine / indicator implementations the chart renders from.
export class AlertEvaluator {
  private states = new Map<string, AlertState>();

  private state(id: string): AlertState {
    let s = this.states.get(id);
    if (!s) { s = fresh(); this.states.set(id, s); }
    return s;
  }

  reset(alertId: string) { this.states.delete(alertId); }

  evaluate(alerts: AlertDef[], candles: Candle[], tf: Timeframe, nowMs = Date.now()): AlertFire[] {
    const fires: AlertFire[] = [];
    if (candles.length < 3) return fires;
    const needStructure = alerts.some((a) => !["price", "rsi", "tick_dominance", "tick_imbalance", "tick_speed"].includes(a.type));
    const ctx: Ctx = {
      candles, tf, last: candles[candles.length - 1],
      structure: needStructure ? StructureEngine.analyze(candles, ALL_FLAGS) : null,
      series: new Map(),
    };
    for (const a of alerts) {
      const st = this.state(a.id);
      if (st.done) continue;
      const fire = this.evalOne(a, st, ctx);
      st.primed = true;
      if (!fire) continue;
      if (nowMs - st.lastFireMs < a.cooldownS * 1000) continue;
      st.lastFireMs = nowMs;
      if (a.repeat === "once") st.done = true;
      fires.push(fire);
    }
    return fires;
  }

  private valueSeries(a: AlertDef, ctx: Ctx): (number | null)[] {
    const key = `${a.type}:${JSON.stringify(a.params)}`;
    let s = ctx.series.get(key);
    if (s) return s;
    const data = { candles: ctx.candles, tf: ctx.tf };
    switch (a.type) {
      case "price": s = ctx.candles.map((c) => c.close); break;
      case "rsi": s = RSI.calculate(data, { period: a.params.period ?? 14 }, "").panel!.lines![0].values; break;
      case "tick_dominance": s = TickDominance.calculate(data, { period: a.params.period ?? 20 }, "").panel!.lines![0].values; break;
      case "tick_imbalance": s = TickImbalance.calculate(data, { period: a.params.period ?? 20 }, "").panel!.histogram!.values; break;
      case "tick_speed": s = TickSpeed.calculate(data, { smooth: a.params.smooth ?? 5 }, "").panel!.lines![0].values; break;
      default: s = [];
    }
    ctx.series.set(key, s);
    return s;
  }

  private evalOne(a: AlertDef, st: AlertState, ctx: Ctx): AlertFire | null {
    const meta = alertMeta(a.type);
    if (!meta) return null;
    const base = { alertId: a.id, type: a.type, price: ctx.last.close, time: ctx.last.time };

    // ---- threshold / crossing alerts -------------------------------------------------
    if (["price", "rsi", "tick_dominance", "tick_imbalance", "tick_speed"].includes(a.type)) {
      const series = this.valueSeries(a, ctx);
      const cur = series[series.length - 1];
      if (cur == null) return null;
      const level = a.params.value;
      const prev = st.lastValue;
      st.lastValue = cur;
      const isAbove = cur > level, isBelow = cur < level;
      let hit = false;
      switch (a.condition) {
        case "above": hit = isAbove && !st.wasTrue; st.wasTrue = isAbove; break;
        case "below": hit = isBelow && !st.wasTrue; st.wasTrue = isBelow; break;
        case "cross_above": hit = prev != null && prev < level && cur >= level; break;
        case "cross_below": hit = prev != null && prev > level && cur <= level; break;
      }
      if (!hit) return null;
      const label = meta.label;
      const verb = meta.conditions.find((c) => c.id === a.condition)?.label.toLowerCase() ?? a.condition;
      return { ...base, value: cur, message: `${a.symbol} ${label} ${verb} ${fmt(level)} (now ${fmt(cur)})`, payload: { level, value: cur, prev } };
    }

    const s = ctx.structure;
    if (!s) return null;
    const timeAt = (i: number) => ctx.candles[i]?.time ?? 0;

    // ---- event alerts: fire on keys not seen before (prime = swallow history) ---------
    const newKeys = (items: { key: string; fire: () => AlertFire }[]): AlertFire | null => {
      let out: AlertFire | null = null;
      for (const it of items) {
        if (st.seen.has(it.key)) continue;
        remember(st.seen, it.key);
        if (st.primed && !out) out = it.fire();
      }
      return out;
    };

    if (a.type === "bos" || a.type === "choch") {
      const want = a.type === "bos" ? "BOS" : "CHoCH";
      return newKeys(s.events.filter((e) => e.type === want && dirOk(a.condition, e.dir === "bull" ? "bullish" : "bearish")).map((e) => ({
        key: `${e.time}-${e.dir}`,
        fire: () => ({ ...base, price: e.price, time: e.time, message: `${a.symbol} ${e.dir === "bull" ? "Bullish" : "Bearish"} ${want} at ${fmt(e.price)}`, payload: { dir: e.dir, level: e.price, from: timeAt(e.from) } }),
      })));
    }

    if (a.type === "cisd" && s.cisd) {
      return newKeys(s.cisd.events.filter((e) => dirOk(a.condition, e.dir === "bull" ? "bullish" : "bearish")).map((e) => ({
        key: `${e.time}-${e.dir}`,
        fire: () => ({ ...base, price: e.level, time: e.time, message: `${a.symbol} ${e.dir === "bull" ? "Bullish" : "Bearish"} CISD confirmed at ${fmt(e.level)}`, payload: { strategy: "CISD", dir: e.dir, level: e.level } }),
      })));
    }

    if ((a.type === "t50" || a.type === "t50_failed" || a.type === "cisd_t50") && s.t50) {
      const thr = (a.params.threshold ?? 50) / 100;
      const t50 = Math.abs(thr - s.t50.config.threshold) < 1e-9 ? s.t50 : T50Engine.analyze(ctx.candles, { ...s.t50.config, threshold: thr });
      let evs = t50.events;
      if (a.type === "t50_failed") evs = evs.filter((e) => e.failed && e.hedge);
      if (a.type === "cisd_t50" && s.cisd) {
        const within = a.params.within ?? 3, idx = new Map(s.cisd.events.map((e) => [e.index, e.dir]));
        evs = evs.filter((e) => { for (let k = 0; k <= within; k++) if (idx.get(e.index - k) === e.dir) return true; return false; });
      }
      return newKeys(evs.filter((e) => dirOk(a.condition, e.dir === "bull" ? "bullish" : "bearish")).map((e) => ({
        key: `${e.time}-${e.dir}-${a.type}`,
        fire: () => a.type === "t50_failed"
          ? ({ ...base, price: e.hedge!.entryPrice, time: timeAt(e.failIndex!), message: `${a.symbol} T50 ${e.dir} FAILED → counter-hedge ${e.hedge!.dir.toUpperCase()} at ${fmt(e.hedge!.entryPrice)} (shadow)`, payload: { strategy: "T50", event: "HEDGE", dir: e.hedge!.dir, threshold: e.threshold, original: e.dir } })
          : ({ ...base, price: e.bar2.close, time: e.time, message: `${a.symbol} ${a.type === "cisd_t50" ? "CISD + " : ""}T50 ${e.dir === "bull" ? "Bullish" : "Bearish"} confirmed (${Math.round(e.threshold * 100)}% · close location ${(e.closeLoc * 100).toFixed(1)}%)`, payload: { strategy: a.type === "cisd_t50" ? "CISD+T50" : "T50", dir: e.dir, threshold: e.threshold, closeLoc: e.closeLoc } }),
      })));
    }

    if (a.type === "fvg_created") {
      return newKeys(s.fvg.filter((g) => dirOk(a.condition, g.dir === "bull" ? "bullish" : "bearish")).map((g) => ({
        key: `${timeAt(g.index)}-${g.dir}`,
        fire: () => ({ ...base, time: timeAt(g.index), message: `${a.symbol} ${g.dir === "bull" ? "Bullish" : "Bearish"} FVG created ${fmt(g.bottom)} – ${fmt(g.top)}`, payload: { dir: g.dir, top: g.top, bottom: g.bottom } }),
      })));
    }

    if (a.type === "fvg_filled") {
      // track gaps created after priming; fire when price trades back into the gap
      for (const g of s.fvg) {
        if (!dirOk(a.condition, g.dir === "bull" ? "bullish" : "bearish")) continue;
        const key = `${timeAt(g.index)}-${g.dir}`;
        if (st.seen.has(key)) continue;
        remember(st.seen, key);
        if (st.primed) st.pendingGaps.set(key, { ...g, time: timeAt(g.index) });
      }
      const last = ctx.last;
      for (const [key, g] of st.pendingGaps) {
        if (last.time <= g.time) continue;
        const filled = g.dir === "bull" ? last.low <= g.top : last.high >= g.bottom;
        if (!filled) continue;
        st.pendingGaps.delete(key);
        return { ...base, message: `${a.symbol} ${g.dir === "bull" ? "Bullish" : "Bearish"} FVG filled at ${fmt(last.close)}`, payload: { dir: g.dir, top: g.top, bottom: g.bottom } };
      }
      return null;
    }

    if (a.type === "eqh" || a.type === "eql") {
      const kind = a.type.toUpperCase();
      return newKeys(s.liquidity.filter((l) => l.kind === kind).map((l) => ({
        key: `${kind}-${l.indices.map(timeAt).join("-")}`,
        fire: () => ({ ...base, message: `${a.symbol} ${kind} formed at ${fmt(l.price)}`, payload: { level: l.price } }),
      })));
    }

    if (a.type === "liquidity_sweep") {
      const current = new Map(s.liquidity.map((l) => [`${l.kind}-${l.indices.map(timeAt).join("-")}`, l]));
      let out: AlertFire | null = null;
      for (const [key, lv] of st.knownLevels) {
        if (current.has(key)) continue;
        st.knownLevels.delete(key);
        const swept = lv.kind === "EQH" ? ctx.last.close > lv.price : ctx.last.close < lv.price;
        const dir = lv.kind === "EQH" ? "bullish" : "bearish";
        if (swept && st.primed && !out && dirOk(a.condition, dir)) {
          out = { ...base, message: `${a.symbol} ${lv.kind} liquidity swept at ${fmt(lv.price)}`, payload: { kind: lv.kind, level: lv.price, dir } };
        }
      }
      for (const [key, lv] of current) st.knownLevels.set(key, lv);
      return out;
    }
    return null;
  }
}
